<script setup lang="ts">
import type { CheckboxModelValue, VCheckboxProps } from "./VCheckbox.types";
import { computed } from "vue";
import ACTooltip from "../ACTooltip/ACTooltip.vue";
import VHelpTooltip from "../HelpTooltip/VHelpTooltip.vue";
import { useFieldId } from "../useFieldId";
import {
  checkboxContainerCva,
  checkboxCustomCva,
  checkboxInputCva,
  checkboxLabelCva,
  checkboxWrapperCva,
} from "./VCheckbox.cva";

defineOptions({
  name: "VCheckbox",
  inheritAttrs: false,
});

const {
  value = true,
  falseValue = false,
  labelLeft = null,
  labelRight = null,
  labelVariant = "light",
  helpText = undefined,
  disabled = false,
  hasPerms = true,
  disabledReason = undefined,
  indeterminate = false,
  decorative = false,
  id: rawId = undefined,
} = defineProps<VCheckboxProps>();

defineSlots<{
  "label-right": { id: string }
}>();

const isDisabled = computed(() => {
  return disabled || !hasPerms || Boolean(disabledReason);
});

const modelValue = defineModel<CheckboxModelValue>({
  default: () => ({ checked: false }),
  type: [Boolean, Array, Object],
});

const id = useFieldId(() => rawId, { inheritFieldId: false });

const classes = computed(() => {
  return {
    container: checkboxContainerCva({
      disabled: isDisabled.value,
    }),
    labelLeft: checkboxLabelCva({
      position: "left",
      variant: labelVariant,
      disabled: isDisabled.value,
    }),
    labelRight: checkboxLabelCva({
      position: "right",
      variant: labelVariant,
      disabled: isDisabled.value,
    }),
    wrapper: checkboxWrapperCva({}),
    input: checkboxInputCva({}),
    custom: checkboxCustomCva({
      disabled: isDisabled.value,
    }),
  };
});
</script>

<template>
  <div :class="[classes.container, $attrs.class]">
    <label
      v-if="labelLeft"
      :for="id"
      :class="classes.labelLeft"
    >
      {{ labelLeft }}
    </label>
    <ACTooltip
      type="edit_field"
      :allow-change="hasPerms"
      :disabled-reason="disabledReason"
    >
      <div class="flex items-center">
        <div :class="classes.wrapper">
          <input
            :id="id"
            v-model="modelValue"
            type="checkbox"
            :class="classes.input"
            :value="value"
            :true-value="value"
            :false-value="falseValue"
            :disabled="isDisabled"
            :name="name"
            :indeterminate="indeterminate"
            :tabindex="decorative ? -1 : undefined"
            :aria-hidden="decorative || undefined"
            v-bind="$attrs"
          >
          <label :for="id" :class="classes.custom" />
        </div>
        <label
          v-if="labelRight || $slots['label-right']"
          :for="id"
          :class="classes.labelRight"
        >
          <template v-if="labelRight">
            {{ labelRight }}
          </template>
          <template v-else>
            <slot :id="id" name="label-right" />
          </template>
        </label>
        <!-- Fixed to the 16px label line: the 20px help button overflows it instead of making the row taller. -->
        <span v-if="helpText" class="ml-1.5 flex h-4 items-center">
          <VHelpTooltip :content="helpText" :subject="labelRight ?? undefined" />
        </span>
      </div>
    </ACTooltip>
  </div>
</template>

<style lang="scss" scoped>
@reference "../../tailwind-reference.css";
.checkbox-wrapper {
  .checkbox-custom {
    height: 16px;
    width: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: solid 1px var(--primary, theme('colors.primary.500'));
    border-radius: 6px;
    line-height: 16px;
    -webkit-font-smoothing: antialiased;
    color: gray;
    background-color: var(--white, theme('colors.white'));
    text-align: center;
    cursor: pointer;
    user-select: none;
    transition: background-color 100ms ease-in-out, border-color 100ms ease-in-out;
  }

  .checkbox-input:focus-visible + .checkbox-custom {
    outline: 2px solid var(--primary, theme('colors.primary.500'));
    outline-offset: 2px;
  }

  .checkbox-input + .checkbox-custom:after {
    content: "";
    opacity: 0;
    background: var(--primary, theme('colors.primary.500'));
    width: 8px;
    height: 6px;
    border: 1.5px solid var(--white, theme('colors.white'));
    border-top: none;
    border-right: none;
    transform: rotate(-45deg);
    margin-top: -2px;
    transition: opacity 100ms ease-in-out, transform 100ms ease-in-out;
  }

  .checkbox-input:indeterminate + .checkbox-custom {
    background-color: var(--primary, theme('colors.primary.500'));
    border-color: var(--primary, theme('colors.primary.500'));
  }

  .checkbox-input:indeterminate + .checkbox-custom:after {
    transform: rotate(0);
    background-color: var(--white, theme('colors.white'));
    width: 50%;
    height: 2px;
    opacity: 1;
    margin-top: 0;
    border: none;
  }

  .checkbox-input:checked + .checkbox-custom {
    background-color: var(--primary, theme('colors.primary.500'));
    border-color: var(--primary, theme('colors.primary.500'));
  }

  .checkbox-input:checked + .checkbox-custom:after {
    opacity: 1;
  }
}
</style>
